import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import NewsletterForm from '@/components/feature/NewsletterForm';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import { authorList } from '@/mocks/authors';

const BASE_URL = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://roameurope.blog';

const schemaJson = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About roameurope.blog — Relationship Advice & Dating Tips',
  description:
    'roameurope.blog is an independent relationship and dating advice blog. Meet our team of writers, therapists, and coaches delivering honest, research-backed content about love, communication, and human connection.',
  url: `${BASE_URL}/about`,
  publisher: {
    '@type': 'Organization',
    name: 'roameurope.blog',
    url: BASE_URL,
    foundingDate: '2024',
    sameAs: [
      'https://twitter.com/roameuropeblog',
      'https://www.instagram.com/roameuropeblog',
      'https://www.pinterest.com/roameuropeblog',
    ],
  },
  mainEntity: {
    '@type': 'Organization',
    name: 'roameurope.blog',
    url: BASE_URL,
    description:
      'Independent relationship and dating advice blog publishing daily articles on love, communication, breakups, marriage, and self-love.',
    foundingDate: '2024',
    knowsAbout: [
      'relationship advice',
      'dating tips',
      'love',
      'communication',
      'self-love',
      'breakup recovery',
      'marriage',
    ],
    employee: authorList.map((a) => ({
      '@type': 'Person',
      name: a.name,
      jobTitle: a.role,
      description: a.bio,
    })),
  },
});

const values = [
  {
    icon: 'ri-shield-check-line',
    title: 'Evidence-Informed',
    body: 'Every article is grounded in psychology research, clinical experience, or verified relationship science. We cite our sources and we don\'t make things up.',
  },
  {
    icon: 'ri-heart-3-line',
    title: 'Genuinely Human',
    body: 'We write for real people navigating real relationships — not perfect Instagram couples. Messy, complicated, and beautifully human is what we\'re here for.',
  },
  {
    icon: 'ri-eye-line',
    title: 'Radically Honest',
    body: 'We don\'t sugarcoat. If something is hard or complicated, we say so. Honesty is the only thing that actually helps people grow.',
  },
  {
    icon: 'ri-global-line',
    title: 'Inclusive Perspectives',
    body: 'Love doesn\'t look the same for everyone. Our writers bring diverse lived experiences across cultures, backgrounds, and relationship styles.',
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Reader-First Always',
    body: 'We write for the person reading, not for clicks. If an article doesn\'t genuinely help someone, it doesn\'t belong on this site.',
  },
  {
    icon: 'ri-lock-line',
    title: 'No Sensationalism',
    body: 'No "10 signs he\'s cheating" listicles designed to trigger insecurity. We write with care and reject content that exploits vulnerability.',
  },
];

const milestones = [
  { year: '2024', label: 'Founded', detail: 'roameurope.blog launched with a mission to bring thoughtful, research-backed relationship writing to readers worldwide.' },
  { year: '2024', label: 'First 10 Articles', detail: 'Our founding writers published the first batch of long-form pieces covering dating, communication, and self-worth.' },
  { year: '2025', label: '50K Monthly Readers', detail: 'Organic search growth brought 50,000 monthly readers — entirely through the quality of the writing, no paid promotion.' },
  { year: '2026', label: '120K Monthly Readers', detail: 'Today, roameurope.blog reaches over 120,000 readers every month across 80+ countries.' },
];

export default function AboutPage() {
  useSeoMeta({
    title: 'About roameurope.blog — Meet Our Writers & Our Mission',
    description:
      'roameurope.blog is an independent relationship and dating advice blog. Meet Sophia Lane, Marcus Reid, and Priya Nair — the writers behind our daily relationship content.',
    url: '/about',
    type: 'website',
    tags: ['about roameurope.blog', 'relationship advice writers', 'dating tips blog team'],
  });

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: schemaJson }}
      />
      <Navbar />

      <main className="bg-white dark:bg-stone-950 transition-colors duration-300">

        {/* ── Hero ── */}
        <section className="relative overflow-hidden pt-28 pb-20">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-10 dark:opacity-5"
            style={{
              backgroundImage: `url('https://public.readdy.ai/ai/img_res/b2a4b500c98c85f2886ed0621667985c.jpg')`,
            }}
          />
          <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center">
            <span className="inline-block px-3 py-1 rounded-full bg-rose-50 dark:bg-rose-900/30 text-rose-500 dark:text-rose-400 text-xs font-semibold tracking-widest uppercase mb-6">
              About Us
            </span>
            <h1
              className="text-4xl md:text-5xl font-bold text-stone-900 dark:text-stone-50 mb-6 leading-tight"
              style={{ fontFamily: "'Lora', serif" }}
            >
              Real People Writing About<br className="hidden md:block" /> Real Relationships
            </h1>
            <p className="text-lg md:text-xl text-stone-500 dark:text-stone-400 leading-relaxed max-w-2xl mx-auto">
              roameurope.blog is an independent relationship and dating advice blog built on one simple belief: honest, thoughtful writing about love can genuinely change people&rsquo;s lives.
            </p>
          </div>
        </section>

        {/* ── Mission ── */}
        <section className="bg-rose-50 dark:bg-rose-900/10 border-y border-rose-100 dark:border-rose-900/30 py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2
                  className="text-3xl font-bold text-stone-800 dark:text-stone-100 mb-5"
                  style={{ fontFamily: "'Lora', serif" }}
                >
                  Our Mission
                </h2>
                <p className="text-stone-600 dark:text-stone-400 leading-relaxed mb-4 text-sm">
                  We started roameurope.blog because we were tired of relationship content that felt hollow — recycled advice, fear-based headlines, and hot takes designed to go viral rather than actually help.
                </p>
                <p className="text-stone-600 dark:text-stone-400 leading-relaxed mb-4 text-sm">
                  <strong className="text-stone-700 dark:text-stone-300">Our mission is simple:</strong> to help people build healthier, more fulfilling relationships — starting with the relationship they have with themselves. Every article we publish is written by a real human with real expertise, grounded in psychology research and real-world experience.
                </p>
                <p className="text-stone-600 dark:text-stone-400 leading-relaxed text-sm">
                  We publish three new articles every day, covering dating, communication, breakups, marriage, and self-love. We write for the person sitting with their phone at midnight wondering if what they&rsquo;re feeling is normal. Spoiler: it usually is.
                </p>
              </div>
              <div className="relative rounded-2xl overflow-hidden h-72 md:h-80">
                <img
                  src="https://readdy.ai/api/search-image?query=cozy%20reading%20nook%20warm%20afternoon%20sunlight%20open%20journal%20notebook%20pen%20coffee%20cup%20soft%20bokeh%20background%20lifestyle%20editorial%20warm%20tones%20inviting&width=600&height=400&seq=about-mission&orientation=landscape"
                  alt="The roameurope.blog team at work — writing about relationships, dating, and love"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-rose-900/20 to-transparent" />
              </div>
            </div>
          </div>
        </section>

        {/* ── Team ── */}
        <section className="py-20">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <h2
                className="text-3xl md:text-4xl font-bold text-stone-800 dark:text-stone-100 mb-4"
                style={{ fontFamily: "'Lora', serif" }}
              >
                Meet Our Writers
              </h2>
              <p className="text-stone-500 dark:text-stone-400 max-w-xl mx-auto text-sm leading-relaxed">
                Every word on this blog is written by a qualified, experienced human being who genuinely cares about the subject. No AI-generated filler, no ghost writers churning out volume — just thoughtful people doing their best work.
              </p>
            </div>

            <div className="space-y-16">
              {authorList.map((author, i) => (
                <div
                  key={author.name}
                  className={`flex flex-col ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-10 items-start`}
                >
                  {/* Photo + quick stats */}
                  <div className="flex flex-col items-center lg:items-start gap-4 flex-shrink-0 w-full lg:w-64">
                    <div className="w-36 h-36 rounded-2xl overflow-hidden">
                      <img
                        src={author.image}
                        alt={`${author.name} — ${author.role} at roameurope.blog`}
                        title={author.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="text-center lg:text-left">
                      <p className="font-bold text-stone-800 dark:text-stone-100 text-lg" style={{ fontFamily: "'Lora', serif" }}>
                        {author.name}
                      </p>
                      <p className="text-rose-500 dark:text-rose-400 text-xs font-semibold uppercase tracking-wide mt-0.5">
                        {author.role}
                      </p>
                      <p className="text-stone-400 dark:text-stone-500 text-xs mt-2">
                        {author.articleCount}+ articles published
                      </p>
                    </div>
                    {/* Social links */}
                    <div className="flex items-center gap-2">
                      {author.social.twitter && (
                        <a href={author.social.twitter} rel="nofollow" aria-label={`${author.name} on Twitter`}
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:bg-rose-100 hover:text-rose-500 dark:hover:bg-rose-900/30 dark:hover:text-rose-400 transition-colors cursor-pointer">
                          <i className="ri-twitter-x-line text-sm" />
                        </a>
                      )}
                      {author.social.instagram && (
                        <a href={author.social.instagram} rel="nofollow" aria-label={`${author.name} on Instagram`}
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:bg-rose-100 hover:text-rose-500 dark:hover:bg-rose-900/30 dark:hover:text-rose-400 transition-colors cursor-pointer">
                          <i className="ri-instagram-line text-sm" />
                        </a>
                      )}
                      {author.social.linkedin && (
                        <a href={author.social.linkedin} rel="nofollow" aria-label={`${author.name} on LinkedIn`}
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:bg-rose-100 hover:text-rose-500 dark:hover:bg-rose-900/30 dark:hover:text-rose-400 transition-colors cursor-pointer">
                          <i className="ri-linkedin-line text-sm" />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Bio + credentials */}
                  <div className="flex-1">
                    <p className="text-stone-600 dark:text-stone-400 leading-relaxed mb-5 text-sm">
                      {author.longBio}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {author.credentials.map((cred) => (
                        <span
                          key={cred}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 text-xs font-medium"
                        >
                          <i className="ri-award-line text-rose-400 text-xs" />
                          {cred}
                        </span>
                      ))}
                    </div>
                    <Link
                      to={`/search?q=${encodeURIComponent(author.name)}`}
                      className="inline-flex items-center gap-1.5 mt-5 text-xs font-semibold text-rose-500 hover:text-rose-600 dark:hover:text-rose-400 transition-colors cursor-pointer"
                    >
                      <i className="ri-article-line" /> Read all articles by {author.name.split(' ')[0]} →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Editorial Values ── */}
        <section className="bg-stone-50 dark:bg-stone-900 border-y border-stone-100 dark:border-stone-800 py-20">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <h2
                className="text-3xl font-bold text-stone-800 dark:text-stone-100 mb-3"
                style={{ fontFamily: "'Lora', serif" }}
              >
                Our Editorial Standards
              </h2>
              <p className="text-stone-500 dark:text-stone-400 max-w-xl mx-auto text-sm leading-relaxed">
                These aren&rsquo;t guidelines we follow to look good — they&rsquo;re the principles that shape every article we publish.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="p-6 rounded-xl bg-white dark:bg-stone-950 border border-stone-200 dark:border-stone-800"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-rose-50 dark:bg-rose-900/30 text-rose-500 mb-4">
                    <i className={`${v.icon} text-lg`} />
                  </div>
                  <h3 className="font-bold text-stone-800 dark:text-stone-100 text-sm mb-2">{v.title}</h3>
                  <p className="text-stone-500 dark:text-stone-400 text-xs leading-relaxed">{v.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Timeline / Story ── */}
        <section className="py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <h2
                className="text-3xl font-bold text-stone-800 dark:text-stone-100 mb-3"
                style={{ fontFamily: "'Lora', serif" }}
              >
                Our Story
              </h2>
              <p className="text-stone-500 dark:text-stone-400 text-sm">
                From a small idea to 120,000+ monthly readers — here&rsquo;s how we got here.
              </p>
            </div>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-rose-100 dark:bg-rose-900/30 -translate-x-1/2" />
              <div className="space-y-10">
                {milestones.map((m, i) => (
                  <div
                    key={m.label}
                    className={`relative flex flex-col md:flex-row ${i % 2 === 0 ? '' : 'md:flex-row-reverse'} items-start md:items-center gap-4 md:gap-8`}
                  >
                    <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-10 md:pl-0`}>
                      <p className="text-rose-500 dark:text-rose-400 text-xs font-bold uppercase tracking-widest mb-1">{m.year}</p>
                      <p className="font-bold text-stone-800 dark:text-stone-100 text-base mb-1" style={{ fontFamily: "'Lora', serif" }}>{m.label}</p>
                      <p className="text-stone-500 dark:text-stone-400 text-xs leading-relaxed">{m.detail}</p>
                    </div>
                    <div className="absolute left-4 md:left-1/2 -translate-x-1/2 top-1 w-3 h-3 rounded-full bg-rose-400 dark:bg-rose-500 border-2 border-white dark:border-stone-950 z-10" />
                    <div className="flex-1 hidden md:block" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Stats bar ── */}
        <section className="bg-stone-800 dark:bg-stone-900 py-12">
          <div className="max-w-5xl mx-auto px-4 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '120K+', label: 'Monthly readers' },
              { value: '90+', label: 'Articles published' },
              { value: '3', label: 'Expert writers' },
              { value: '80+', label: 'Countries reached' },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-3xl font-bold text-rose-400 mb-1" style={{ fontFamily: "'Lora', serif" }}>{s.value}</p>
                <p className="text-xs text-stone-400 uppercase tracking-widest">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Disclosure & AdSense ── */}
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <h2
              className="text-2xl font-bold text-stone-800 dark:text-stone-100 mb-5"
              style={{ fontFamily: "'Lora', serif" }}
            >
              Advertising &amp; Transparency
            </h2>
            <div className="space-y-3 text-sm text-stone-600 dark:text-stone-400 leading-relaxed">
              <p>
                roameurope.blog displays advertisements served by <strong className="text-stone-700 dark:text-stone-300">Google AdSense</strong> and may include sponsored content and affiliate links. These help us keep all our articles free for readers. We are fully compliant with Google AdSense policies and FTC disclosure requirements.
              </p>
              <p>
                We never allow advertising to influence our editorial content. Every article is written independently, based on research and expertise — not on what our advertisers want to promote.
              </p>
              <p>
                For advertising and sponsorship enquiries, visit our <Link to="/advertise" className="text-rose-500 hover:underline">Advertise page</Link>. For privacy information about how ads use cookies, see our <Link to="/privacy-policy" className="text-rose-500 hover:underline">Privacy Policy</Link>.
              </p>
            </div>

            <div className="mt-8 p-5 rounded-xl bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-800">
              <h3 className="text-sm font-bold text-stone-700 dark:text-stone-300 mb-2 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center text-rose-400">
                  <i className="ri-information-line" />
                </div>
                Medical &amp; Professional Disclaimer
              </h3>
              <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed">
                Content on roameurope.blog is for informational and educational purposes only. It is not a substitute for professional psychological, therapeutic, or medical advice. If you are experiencing a mental health crisis, please contact a qualified professional or call the <strong>988 Suicide &amp; Crisis Lifeline</strong> (US) by dialing or texting <strong>988</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* ── CTA newsletter ── */}
        <div className="pb-4">
          <NewsletterForm variant="banner" />
        </div>

        {/* ── Contact CTA ── */}
        <section className="py-16">
          <div className="max-w-2xl mx-auto px-4 md:px-8 text-center">
            <h2
              className="text-2xl font-bold text-stone-800 dark:text-stone-100 mb-3"
              style={{ fontFamily: "'Lora', serif" }}
            >
              Get In Touch
            </h2>
            <p className="text-stone-500 dark:text-stone-400 text-sm mb-6 leading-relaxed">
              Have a question, suggestion, or just want to say something? We genuinely read every message.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white font-semibold px-7 py-3 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line" /> Contact Us
            </Link>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
